import Badge from '@/components/shared/Badge/Badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowRight, Link as LinkIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { NoProducts } from '@/components/shared/NoProducts/NoProducts';

interface ProductsGridProps {
  products: any[];
  onProductClick?: (product: any) => void;
  actionLabel?: string;
}

export const ProductsGrid = ({ products, onProductClick, actionLabel }: ProductsGridProps) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const getInitials = (name) => {
    const parts = name.split(' ');
    const firstInitial = parts[0][0];
    const lastInitial = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  const handleClick = (product) => {
    if (onProductClick) {
      onProductClick(product);
    } else {
      navigate(`/products/edit-product/${product.id}/geral`);
    }
  };

  if (!products?.length) {
    return (
      <NoProducts variant='no-results' />
    );
  }
  return (
    <section className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6'>
      {products.map((product) => (
        <Card
          key={product.id}
          data-cy='product-card'
          onClick={() => handleClick(product)}
          className='relative bg-[#18181B] cursor-pointer w-full 2xl:max-w-full text-white rounded-xl shadow-lg p-4 flex flex-col gap-4 transition-all duration-300 group'
        >
          <div
            className='absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300'
            style={{
              background:
                'linear-gradient(to right, #1F1F23 70%, #404a9399 100%)',
            }}
          ></div>
          <article className='relative z-10 flex items-center gap-4'>
            <Avatar>
              <AvatarImage src={product?.images?.[0]?.url} alt={product.name} />
              <AvatarFallback>{getInitials(product.name)}</AvatarFallback>
            </Avatar>
            <div className='line-clamp-1'>
              <h2 className='text-lg line-clamp-1 font-semibold'>
                {product.name}
              </h2>
              <p className='text-sm text-gray-400'>{product.category?.label}</p>
            </div>
          </article>
          <div className='flex-grow'></div>
          <footer className='relative z-10 flex justify-between items-center p-2 rounded-lg'>
            {actionLabel ? (
              <Button
                variant='outline'
                size='sm'
                className='w-full'
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick(product);
                }}
              >
                <LinkIcon className='h-4 w-4 mr-2' />
                {actionLabel}
              </Button>
            ) : (
              <>
                <Badge status={product.status} />
                <ArrowRight className='w-5 h-5 text-gray-400 opacity-0 group-hover:opacity-100 group-hover:text-white transition-all duration-300' />
              </>
            )}
          </footer>
        </Card>
      ))}
    </section>
  );
};
